import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { SocietySideBarCallout } from '../SocietySideBarCallout'
import { SocietyTypes } from '../../../types/society-type'
import { ISocietiesModel } from '../../../interfaces/models/societies.interfaces'
import { getSocietyActiveTrialStatus } from '../../../state/models/society'
import { unitQueries } from '../../../api/units'
import { i18nSocietyType } from '../../../i18n'

export const useManagementCallout = ({
  societyLocked,
  society,
}: {
  societyLocked: boolean
  society?: ISocietiesModel
}): SocietySideBarCallout | null => {
  const { translate } = useAppTranslation()
  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id)
  )

  const unitsForSociety =
    societyUnits?.filter((unit) => !unit.isUnassigned) || []
  if (!society) return null
  if (society.societyTypeDisplayName === SocietyTypes.NEIGHBOURHOOD) {
    return null
  }

  const unitsForSocietyWithoutResidents = unitsForSociety.filter(
    (_unit) => _unit.residentsList && _unit.residentsList.length === 0
  )

  const getManagementCallout = (): SocietySideBarCallout | null => {
    const { billingUnits } = society
    const unitCountForSociety = unitsForSociety.length

    const numberOfUnitsWithResidents =
      unitCountForSociety - unitsForSocietyWithoutResidents.length

    if (societyLocked) {
      return {
        text: translate('societySideBar.callouts.societyLocked.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        }),
        path: reverseUrl('management:billing-payment', {
          id: society._id,
        }),
        warning: true,
      }
    }
    const { active: activeTrial, daysLeft } =
      getSocietyActiveTrialStatus(society)

    if (activeTrial && daysLeft && daysLeft > 0) {
      return {
        text:
          daysLeft > 1
            ? translate(
                'societySideBar.callouts.societyTrial.titleDaysPlural',
                {
                  days: daysLeft,
                }
              )
            : translate('societySideBar.callouts.societyTrial.titleDays', {
                days: daysLeft,
              }),

        path: reverseUrl('management:billing-payment', {
          id: society._id,
        }),
      }
    }

    if (
      billingUnits &&
      unitCountForSociety < billingUnits * 0.9 &&
      unitCountForSociety
    ) {
      return {
        text: translate('societySideBar.callouts.incompleteRegister.title', {
          context: i18nSocietyType(society.societyTypeDisplayName),
        }),
        path: reverseUrl('management:register-guidance', { id: society._id }),
      }
    }

    if (
      numberOfUnitsWithResidents < unitCountForSociety * 0.9 &&
      unitCountForSociety
    ) {
      return {
        text: `Endast ${numberOfUnitsWithResidents} av ${unitsForSociety.length} hushåll inloggade`,
        path: reverseUrl('management:member-guidance', { id: society._id }),
      }
    }

    if (
      !(numberOfUnitsWithResidents < unitCountForSociety * 0.9) &&
      !(billingUnits && unitCountForSociety < billingUnits * 0.9) &&
      unitCountForSociety
    ) {
      return null
    }

    return null
  }

  return getManagementCallout()
}
